import React from "react";
import { Link } from "react-router-dom";
import Alert from "web-ui/components/internal/Alert";
import Button from "web-ui/components/internal/Button";
import Drawer from "web-ui/components/internal/Drawer";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Table from "web-ui/components/internal/Table";
import TextInput from "web-ui/components/internal/TextInput";

import { type VoiceListVoice } from "../../api/generalTts";
import {
  getCustomVoiceEndpointStatuses,
  getTtsGenerationAudio,
  spinUpCustomVoiceEndpoint,
} from "../../api/ttsGenerationBackend";

import AccordionPanel from "./AccordionPanel";
import styles from "./VoiceDrawer.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  className?: string;
  exampleSentence?: string;
  open?: boolean;
  onRequestClose?: () => void;
  voice?: VoiceListVoice;
}

const VoiceDrawer = ({
  className,
  exampleSentence,
  open,
  onRequestClose,
  voice,
}: Props) => (
  <Drawer
    className={className}
    headerText={voice?.alias}
    modal={false}
    onRequestClose={onRequestClose}
    open={open}
  >
    <VoiceDrawerContents
      exampleSentence={exampleSentence}
      key={voice?.alias}
      voice={voice}
    />
  </Drawer>
);

// eslint-disable-next-line complexity
const VoiceDrawerContents = ({
  exampleSentence = "",
  voice,
}: Pick<Props, "exampleSentence" | "voice">) => {
  const [azureError, setAzureError] = React.useState<string>();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [previewText, setPreviewText] = React.useState(exampleSentence);

  const isUnavailable = voice?.status === "UNAVAILABLE";

  const createEvaluationParams = React.useMemo(() => {
    if (!voice) {
      return undefined;
    }
    const params = new URLSearchParams();
    params.set("languageId", voice.languageId.split("-")[0]);
    params.set("provider", voice.provider.toLowerCase());
    params.set("voiceName", voice.alias);
    return params;
  }, [voice]);

  const handlePlay = async () => {
    if (!voice) {
      return;
    }
    setIsPlaying(true);
    try {
      if (voice.character && voice.provider === "AZURE") {
        const status = await (async () => {
          // It looks like the backend returns the alias when fetching by voice name,
          // so check both to be sure.
          const statuses = await getCustomVoiceEndpointStatuses([
            voice.alias,
            voice.name,
          ]);
          return statuses[voice.name] ?? statuses[voice.alias];
        })();
        switch (status) {
          case "ACTIVE":
            setAzureError(undefined);
            break;
          case "SPINNING_DOWN":
          case "SUSPENDED":
            spinUpCustomVoiceEndpoint(voice.name);
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "SPINNING_UP":
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "UNKNOWN":
            setAzureError("Unknown error");
            return;
          default:
            return;
        }
      } else {
        setAzureError(undefined);
      }
      const mp3Data = await getTtsGenerationAudio({
        languageId: voice.languageId,
        text: previewText,
        voiceName: voice.name,
      });
      const url = URL.createObjectURL(mp3Data);
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener("ended", () => URL.revokeObjectURL(url));
      await audio.play();
    } finally {
      setIsPlaying(false);
    }
  };

  return (
    <>
      {isUnavailable ? (
        <Alert
          className={styles.alert}
          color="amber"
          description="Voice is marked unavailable by provider"
          leadingVisual={iconFactory("warning")}
        />
      ) : null}
      <div className={styles.panels}>
        <AccordionPanel header="Preview audio">
          <div className={styles.preview}>
            <Button
              isIconOnly={true}
              onClick={handlePlay}
              size="s"
              state={
                isPlaying
                  ? "loading"
                  : !isUnavailable && previewText
                    ? "enabled"
                    : "disabled"
              }
            >
              <Icon color="inherit" name="play" size="s" variant="inverse" />
            </Button>
            <TextInput
              className={styles.input}
              helperText={azureError}
              onChange={e => setPreviewText(e.value)}
              placeholder="Add text to preview the voice"
              size="s"
              state={azureError === undefined ? "enabled" : "error"}
              value={previewText}
            />
          </div>
        </AccordionPanel>
        {voice === undefined ? null : (
          <AccordionPanel header="About">
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                <tr>
                  <td>Voice name</td>
                  <td className={styles["table-value"]}>{voice.alias}</td>
                </tr>
                <tr>
                  <td>Model ID</td>
                  <td className={styles["table-value"]}>{voice.name}</td>
                </tr>
                <tr>
                  <td>Provider</td>
                  <td className={styles["table-value"]}>
                    {formatProvider(voice.provider)}
                  </td>
                </tr>
                <tr>
                  <td>Language</td>
                  <td className={styles["table-value"]}>
                    {formatLanguage(voice.languageId)}
                  </td>
                </tr>
                <tr>
                  <td>Multilingual</td>
                  <td className={styles["table-value"]}>
                    {voice.isMultilingual ? "Yes" : "No"}
                  </td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td className={styles["table-value"]}>
                    {voice.gender === undefined
                      ? "N/A"
                      : titleCaseFormatter(voice.gender)}
                  </td>
                </tr>
                <tr>
                  <td>Character</td>
                  <td className={styles["table-value"]}>
                    {(voice.character ?? "CHARACTER_UNKNOWN") ===
                    "CHARACTER_UNKNOWN"
                      ? "N/A"
                      : formatCharacter(voice.character ?? "CHARACTER_UNKNOWN")}
                  </td>
                </tr>
                <tr>
                  <td>Age</td>
                  <td className={styles["table-value"]}>
                    {voice.age === undefined
                      ? "N/A"
                      : titleCaseFormatter(voice.age)}
                  </td>
                </tr>
                {voice.providerParameters ? (
                  <>
                    {(voice.providerParameters.rate ?? null) === null ? null : (
                      <tr>
                        <td>Rate</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.rate}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.pitch ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Pitch</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.pitch}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.volume ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Volume</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.volume}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.similarity_boost ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Similarity boost</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.similarity_boost}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.stability ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Stability</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.stability}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.style ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Style</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.style}
                        </td>
                      </tr>
                    )}
                    {(voice.providerParameters.use_speaker_boost ?? null) ===
                    null ? null : (
                      <tr>
                        <td>Use speaker boost</td>
                        <td className={styles["table-value"]}>
                          {voice.providerParameters.use_speaker_boost
                            ? "Yes"
                            : "No"}
                        </td>
                      </tr>
                    )}
                  </>
                ) : null}
              </tbody>
            </Table>
          </AccordionPanel>
        )}
        <AccordionPanel header="Quality">
          <span className={styles["text-empty"]}>
            This section hasn&apos;t been implemented yet
          </span>
        </AccordionPanel>
        <AccordionPanel header="Evaluations">
          <Link
            className={styles.link}
            target="_blank"
            to={`https://tts-evaluation.duolingo.com/admin/create-evaluation-v2?${createEvaluationParams?.toString()}`}
          >
            Create new
          </Link>
        </AccordionPanel>
        {voice === undefined ? null : (
          <>
            <AccordionPanel header="Notes">
              <span
                className={voice.notes ? styles.text : styles["text-empty"]}
              >
                {voice.notes || "No notes added"}
              </span>
            </AccordionPanel>
            <AccordionPanel header="History">
              <Table className={styles.table} outerBorder={false} size="s">
                <tbody>
                  {isUnavailable ? (
                    <tr>
                      <td>Marked unavailable</td>
                      <td className={styles["table-date"]}>N/A</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>Voice added</td>
                    <td className={styles["table-date"]}>
                      {voice.addedAt?.toDateString() ?? "N/A"}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </AccordionPanel>
          </>
        )}
      </div>
    </>
  );
};

export default VoiceDrawer;
